import { useState, useRef, useEffect } from "react";
// BRIEF 104 §1a — `provisionalLevel` left this list with the level letter, exactly
// as it left `EisenhowerDialog` in 102 §2e: its only reader was the lookup that
// fed the letter, and the letter is gone.
import { eisenQuadrant, EISEN_QUADRANTS, edgeTint } from "./utils"; // 190 round 7 [AF]
import { EisenBadge, EISEN_LABELS, eisenLabel } from "./EisenBadge";
import { useTranslation } from "./i18n";
// BRIEF 101 §3 — one date block, per §0's ruling.
import DateBlock from "./components/DateBlock";
/* ── BRIEF 105 §5a — THE CARDS COME UP TO THE AGENDA LIST'S SIZES ───────────
   The mapping is `EisenhowerDialog`'s, applied identically here because 104 §1
   rules the two matrices one shape — see that file's header for the table that
   says which agenda element each size came from.
   THE `compact` ARM IS UNTOUCHED, deliberately and for 104 §A1's reason: its
   own comment rules it "a drag surface, not a reading surface — hit targets
   over legibility", it renders no date and no counter, and it lives in a 330px
   pop-out column where 11.5px text would overflow rather than read better. Its
   `fontSize: 9` literal is left with it. */
// BRIEF 104 §1b/§1c — THE SAME TWO DEFINITIONS `EisenhowerDialog` READS, and §1d
// is why they are imports rather than copies: if either had needed a variant
// here, that would have been the signal 102's sharing was incomplete. Neither
// did — both are total over a task and neither knows which surface is asking.
import { taskUrgencyColor } from "./lib/taskUrgency";
import LinkIcon from "./components/LinkIcon"; // 111 §5c
import { resolveMatter } from "./lib/matterLabel"; // 191 §C
import { dayDiff, dayCountdownLabel } from "./lib/dayCountdown";
import { RAD, FS, EISEN_RETREAT, MATRIX_GAP, MATRIX_QUAD_PAD } from "./lib/tokens"; // 206B §3 — the retreat, both boards; 206B §2 — this board's two figures, named (206C §1: no cap derives from them now)
import { useEisenRows } from "./lib/eisenRows"; // 206 §4 — the two rows, measured

// The same clock `taskUrgencyColor` reads, so a card's words and its colour
// cannot disagree — the line `EisenhowerDialog` carries for the same reason.
const todayISO = () => new Date().toISOString().slice(0, 10);

// Inline mini Eisenhower matrix for the Tasks section.
// Renders a compact 2×2 quadrant grid with draggable task cards.
// Props:
//   tasks      — filtered list of tasks to display
//   ca         — optional cases array (for resolving case titles on cards)
//   tkTypes    — optional activity type definitions (for rendering type badges)
//   onMove     — called (taskId, quadrantKey) when a task is dropped in a quadrant.
//                INVARIANT: handlers must persist this to per-user preference state only,
//                never mutate the underlying task record.
//   onTaskClick — optional, called with the task when a card is clicked
export const TaskMatrix = ({
  tasks,
  ca,
  /* 111 §5c — the collective cases, for a cc task's itemLinks feed; optional
     (a host without it renders no icon on cc rows). */
  cc,
  tkTypes,
  // BRIEF 104 §1a — `priMap` IS GONE FROM THIS COMPONENT. It existed to resolve
  // the level letter and had no other reader; the three hosts that passed it
  // (Dashboard, CaseTasksSection, CompactColumn) no longer do. It remains a prop
  // of `CaseTaskRow` and `CaseEventRow`, which still draw levels — this is the
  // same split 102 §5e drew in `AgendaView`, where the helpers stayed because the
  // EVENT row still had a letter to draw.
  onMove,
  onTaskClick,
  getQuadrant,
  onClose,
  // Phase 29 Stage-3 C37 — compact variant for the pop-out's panel (C56:
  // rendered inline in the column, chrome-less): it fills the panel height
  // (no 360px floor), tighter quadrants, and single-line cards. C57 — the
  // cards drop the Eisenhower badge + priority level (the quadrant already
  // says both); row 1 = type tag + case title, row 2 = the task text,
  // both C11-ellipsised. CV/CCV/Dashboard matrices never pass `compact`
  // and are untouched. It's a drag surface, not a reading surface — hit
  // targets over legibility.
  compact = false,
  /* 191 C.2c (ruling C.0, carried) — EisenTaskCard's own prop, the same
     shape: a case's own matrix does not repeat the case name on every card
     (CaseTasksSection passes false); the Dashboard's and the compact column's
     matrices mix matters and keep it, resolved through resolveMatter so a
     collective case's task names its collective case. Default true. */
  showMatter = true,
}: any) => {
  const { t } = useTranslation();
  const [dragId, setDragId] = useState(null);
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!onClose) return;
    let attached = false;
    const handler = (e: any) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Defer attaching the listener so the click that opened us doesn't re-close us
    const timerId = setTimeout(() => {
      document.addEventListener("click", handler);
      attached = true;
    }, 0);
    return () => {
      clearTimeout(timerId);
      if (attached) document.removeEventListener("click", handler);
    };
  }, [onClose]);

  /* 206 §4 — the grid is `rootRef`'s own element, and its four children are
     the quadrants in reading order. The gap is the same expression the style
     below writes, because the rule needs the figure that is actually between
     the rows. */
  useEisenRows(rootRef, compact ? 4 : MATRIX_GAP);

  const bucketOf = getQuadrant || eisenQuadrant;
  const tasksByQ: any = { ui: [], u: [], i: [], s: [] };
  tasks.forEach((t: any) => {
    tasksByQ[bucketOf(t)].push(t);
  });

  return (
    <div
      ref={rootRef}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        /* 206 §4 [RULED — Laurent, SUPERSEDING 205 §4] — THE TWO ROWS ARE
           MEASURED, NOT DECLARED. 205 §4 put `auto auto` here and the ruling
           reverses half of it: content-sized rows satisfy "no quadrant left
           mostly empty while another overflows", and they FAIL "when there is
           still slack the two rows match", because `align-content: stretch`
           distributes leftover space equally rather than equalising the rows.
           Neither `1fr 1fr` nor `auto auto` can express a rule whose branch
           depends on comparing the two rows' content, so the template is
           written in pixels by `useEisenRows` (lib/eisenRows.ts), which holds
           the whole rule, the arithmetic and the reasoning; this value is the
           starting template the measurement replaces on the first layout. */
        gridTemplateRows: "auto auto",
        gap: compact ? 4 : MATRIX_GAP, // 206B §2 — named in lib/tokens.ts
        // compact: a modest floor so the four quadrants stay usable drop
        // targets; the anchored panel caps overall height and scrolls.
        // 206 §4 — THE FLOOR STAYS, AND IT IS THE ONLY FLOOR: it bounds the
        // BOARD, and `useEisenRows` reads the board it produces, so at 360
        // with little in it the two rows come out matched at 177 each. It is
        // deliberately NOT passed down as a per-row minimum — doing that took
        // room away from a crowded row on a board that had plenty (the
        // arithmetic is in eisenRows.ts's header).
        minHeight: compact ? 220 : 360,
        marginBottom: compact ? 0 : 12,
      }}
    >
      {EISEN_QUADRANTS.map((q) => (
        <div
          key={q.k}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const id = +e.dataTransfer.getData("text/plain");
            if (id && onMove) onMove(id, q.k);
            setDragId(null);
          }}
          style={{
            /* 190 round 8 [AO] — the tint stays, the outline, the title's
               underline and the counter go; the same three as
               EisenhowerDialog (104 §1: the two matrices are one shape). */
            background: q.bg,
            borderRadius: RAD.r8,
            padding: compact ? 4 : MATRIX_QUAD_PAD, // 206B §2 — named in lib/tokens.ts
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 6,
              paddingBottom: 4,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                /* BRIEF 105 §5a — the quadrant heading, one step under the
                   agenda's month name. Identical to `EisenhowerDialog`'s,
                   because 104 §1 rules the two matrices the same shape. */
                fontSize: FS.s13,
                fontWeight: 700,
                color: q.accent,
                textTransform: "uppercase",
                letterSpacing: ".3px",
              }}
            >
              {eisenLabel(q.k)}
            </span>
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              /* 205 §3b [RULED — Laurent's correction: the padding BETWEEN the
                 cards too] — THE QUADRANT'S CARD LIST, 4 → 8 with `CARD_PAD`.
                 Its own base is 4 and not 6 (the matrix is denser than the
                 dialog), and it doubles from ITS base. The other three `gap`s
                 in this file are the quadrant HEADING row (:146) and the card's
                 own one-line row (:242, :259 — a chip beside its text): all
                 three deliberately untouched. */
              gap: 8,
              /* 206B §3 [RULED — Laurent: "OK, but do the same in case
                 eisenhower"] — THE CARDS STAND ONE CHARACTER IN FROM THE
                 HEADING, AS THE DRAWER'S DO (206 §3). The quadrant's padding is
                 shared by the heading row and this list, which is why they were
                 flush; the retreat belongs on THIS element alone, so the
                 heading does not move and the cards and the empty-state line
                 (both children of this list) do. A MARGIN, for the reason 206
                 §5 gave: `box-sizing: border-box` is global, so a padding would
                 be eaten by any width cap. ONE ELEMENT, THREE HOSTS: this list
                 renders in a case's Tasks section, the Dashboard's matrix and
                 the compact column, and the retreat lands in all three. */
              marginLeft: EISEN_RETREAT,
            }}
          >
            {tasksByQ[q.k].length === 0 && (
              <p
                style={{
                  /* §5a — it stands where a card would. */
                  fontSize: FS.s11_5,
                  color: "var(--tm)",
                  fontStyle: "italic",
                  textAlign: "center",
                  margin: "8px 0",
                }}
              >
                {t("matrix.drop_tasks_here", { fallback: "Drop tasks here" })}
              </p>
            )}
            {tasksByQ[q.k].map((task: any) => {
              /* 191 §C — the matter through the one resolver (the LinkIcon
                 reads the same row). */
              const cs2 = resolveMatter(task, ca, cc);
              const tkt = tkTypes
                ? tkTypes.find((x: any) => x.code === task.type)
                : null;
              /* BRIEF 104 §1b — the urgency colour, from the ONE definition
                 102 §2d shared. Not a third copy: `EisenhowerDialog` reads the
                 same function and so does the task list's title cell. */
              const clr = taskUrgencyColor(task);
              /* §1c — the day counter, in the form 102 §2f settled, INCLUDING
                 its convention: a FUTURE date reads `-3d`, because it is a
                 countdown. Same clock as the colour above. */
              const days = dayCountdownLabel(dayDiff(task.dueDate, todayISO()), t);
              return (
                <div
                  key={task.id}
                  data-lnk={"task-" + task.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", String(task.id));
                    e.dataTransfer.effectAllowed = "move";
                    setDragId(task.id);
                  }}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => onTaskClick && onTaskClick(task)}
                  style={{
                    background: "var(--bc)",
                    border: "1px solid var(--br)",
                    /* 190 round 7 [AF] — the light tint, one derivation (utils). */
                    borderLeft: `3px solid ${edgeTint(q.accent)}`,
                    borderRadius: RAD.r5,
                    padding: "4px 6px",
                    cursor: "pointer",
                    /* §5a — the agenda row's own text size. */
                    fontSize: FS.s11_5,
                    opacity: dragId === task.id ? 0.4 : 1,
                    boxShadow: "0 1px 2px rgba(0,0,0,.25)",
                    flexShrink: 0,
                    /* BRIEF 102 §2c — the block was mounted inside this card's
                       one-line tag strip and the strip grew to its height. That
                       fix stays.
                       BRIEF 104 §A1 — AND THE CARD IS NOW ONE LINE, the same
                       shape as `EisenhowerDialog`'s, because §1 rules the two
                       matrices identical and A1 changed what that shape is:
                         [type] [matrix icon] [case name] [title] ……… [days] [date]
                       `overflow: hidden` is A1b's floor — a one-line card whose
                       title overflows its quadrant is worse than the three-line
                       card, so the card cannot be pushed wider than its column. */
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    overflow: "hidden",
                  }}
                >
                  {/* BRIEF 104 §A1 — ONE LINE, and `compact` keeps its own shape.
                      The full variant is A1a's row of siblings; the 330px pop-out
                      arm below stays two lines because its own comment rules it
                      "a drag surface, not a reading surface — hit targets over
                      legibility", it renders no date and no counter, and A1's
                      glance was on the reading surfaces. Said rather than
                      assumed. */}
                  {compact ? (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          marginBottom: 2,
                        }}
                      >
                        {tkt && (
                          <span
                            style={{
                              fontSize: FS.s8,
                              padding: "1px 3px",
                              borderRadius: RAD.r3,
                              background: "var(--as)",
                              color: "var(--tp)",
                              fontFamily: "'JetBrains Mono',monospace",
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          >
                            {task.type}
                          </span>
                        )}
                        {/* Phase 29 Stage-3 C57 — compact (pop-out-only; CV/CCV
                            never pass `compact`) drops the Eisenhower badge and
                            the priority level (the quadrant the card sits in
                            already says both) and puts the CASE TITLE on row 1
                            next to the type tag, CSS-ellipsised per the C11
                            rules (no JS slicing). */}
                        {showMatter && cs2 && (
                          <span
                            style={{
                              fontSize: FS.s8_5,
                              color: "var(--ac)",
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cs2.title}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          /* §1b — the colour reaches the compact arm too. */
                          color: clr || "var(--tp)",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.3,
                        }}
                      >
                        {task.title}
                      </div>
                    </div>
                  ) : (
                    <>
                      {tkt && (
                        <span
                          style={{
                            /* §5a — `etTag`'s size AND its padding. */
                            fontSize: FS.s9_5,
                            padding: "2px 6px",
                            borderRadius: RAD.r3,
                            background: "var(--as)",
                            color: "var(--tp)",
                            fontFamily: "'JetBrains Mono',monospace",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {task.type}
                        </span>
                      )}
                      <span
                        title={
                          t("matrix.case_matrix_prefix", {
                            fallback: "Case Matrix",
                          }) +
                          ": " +
                          (eisenLabel(eisenQuadrant(task)) || "")
                        }
                        style={{
                          flexShrink: 0,
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        {/* §5a — `CaseTaskRow`'s own non-compact badge size. */}
                        <EisenBadge q={eisenQuadrant(task)} size={12} />
                      </span>
                      {/* 111 §5c — the Link icon; in-place highlight, no
                          scroll assist (v-f): the quadrants are one screen. */}
                      <LinkIcon
                        matterRow={cs2 /* 191 §C — the same resolved row */}
                        item={{ k: "task", id: task.id }}
                        scroll={false}
                      />
                      {/* §A1c — the case name gives way SECOND: `flexShrink: 1`
                          against the title's 3, plus a `maxWidth` so it can never
                          take the line on its own. Same order as the dialog's. */}
                      {showMatter && cs2 && (
                        <span
                          style={{
                            flex: "0 1 auto",
                            minWidth: 0,
                            maxWidth: "40%",
                            /* §5a — the agenda's case-ref size, which is what
                               `EisenhowerDialog` already used. */
                            fontSize: FS.s9_5,
                            color: "var(--ac)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={cs2.title}
                        >
                          {cs2.title}
                        </span>
                      )}
                      {/* §A1b — the title is the adaptive part: it grows into
                          whatever the fixed children leave and shrinks first when
                          there is nothing to leave. 102 §1c's three properties are
                          present, `minWidth: 0` included, and the card is
                          `overflow: hidden` so a quadrant cannot be pushed wider. */}
                      <span
                        style={{
                          flex: "1 3 auto",
                          minWidth: 0,
                          /* §5a — the agenda row's title size, and a TOKEN: this
                             was a bare `10` while every neighbour used `FS`. */
                          fontSize: FS.s11_5,
                          color: clr || "var(--tp)",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={task.title}
                      >
                        {task.title}
                      </span>
                      {days && (
                        <span
                          style={{
                            flexShrink: 0,
                            /* §5a — the agenda's own countdown size
                               (AgendaView.tsx:623). */
                            fontSize: FS.s13,
                            /* BRIEF 104 §A2a — BOLD AT EVERY CONSUMER, and the
                               census says only two consumers needed it. All four
                               call `dayCountdownLabel`, which returns a STRING
                               and no styling, so weight is entirely the
                               caller's:
                                 CaseEventRow.tsx:517   fontWeight 700  ALREADY
                                 AgendaView.tsx:622     fontWeight 700  ALREADY
                                 EisenhowerDialog       600 → 700
                                 TaskMatrix (here)      600 → 700
                               So §A2b's reach warning — that bolding
                               `CaseEventRow` would reach CV/CCV, AgendaView:490
                               and Dashboard:1132 — describes a change that does
                               not happen: those three have rendered 700 since
                               before the helper existed. The two odd ones out
                               were the matrix cards, which 102 §2f and 104 §1c
                               wrote at 600. Size and colour unchanged; weight
                               only. */
                            fontWeight: 700,
                            color: clr || "var(--tm)",
                            lineHeight: 1.3,
                          }}
                        >
                          {days}
                        </span>
                      )}
                    </>
                  )}
                  {/* BRIEF 101 §3 — the block, per §0's ruling: this is a card
                      read at a glance, not a dense list. Only the FULL variant
                      reaches here — `compact` (the 330px pop-out column, :213)
                      renders no due date at all, so §3a's width question stays
                      hypothetical and is answered from this file rather than
                      inherited. BRIEF 102 §2c moved it OUT of the tag strip and
                      beside the whole text column. */}
                  {!compact && task.dueDate && (
                    <DateBlock
                      date={task.dueDate}
                      /* BRIEF 104 §3a — the day carries the red, from the `clr`
                         this card already computed for its title. */
                      dayColor={clr}
                      style={{ flexShrink: 0 }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
